import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { signIn, signUp } from '../api';

const { FiTarget, FiMail, FiLock, FiUser, FiArrowRight, FiAlertCircle, FiLoader, FiArrowLeft, FiZap } = FiIcons;

const AuthPage = ({ onAuth, onBack }) => {
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState('basic');

  useEffect(() => {
    // Check URL parameters for successful payment redirection
    const params = new URLSearchParams(window.location.search);
    const modeParam = params.get('mode');
    const planParam = params.get('plan');
    
    if (modeParam === 'signup' || planParam === 'pro') setMode('signup');
    if (planParam === 'pro') setPlan('pro');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = mode === 'signin' 
        ? await signIn(email, password) 
        : await signUp(email, password, name, plan);
      onAuth(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sonar-bg flex flex-col items-center justify-center px-4 relative">
      {onBack && (
        <button onClick={onBack} className="absolute top-6 left-6 text-slate-500 hover:text-white flex items-center gap-2 text-xs font-bold uppercase tracking-widest transition-colors z-50">
          <SafeIcon icon={FiArrowLeft} className="text-lg" /> Back to Home
        </button>
      )}
      
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl border border-sonar-green/30 bg-sonar-green/5 mb-5">
            <SafeIcon icon={FiTarget} className="text-sonar-green text-3xl" />
          </div>
          <h1 className="text-3xl font-black text-white tracking-tighter uppercase">Credit<span className="text-sonar-green">Sonar</span></h1>
        </div>

        <div className="bg-sonar-card border border-sonar-border rounded-2xl p-8 shadow-2xl">
          {plan === 'pro' && mode === 'signup' && (
            <div className="mb-6 p-4 bg-sonar-green/10 border border-sonar-green/30 rounded-xl flex items-center gap-3">
              <SafeIcon icon={FiZap} className="text-sonar-green text-xl" />
              <div>
                <p className="text-[10px] font-black text-sonar-green uppercase tracking-widest">Payment Verified</p>
                <p className="text-xs text-white font-mono">Provisioning Pro Access...</p>
              </div>
            </div>
          )}

          <div className="flex gap-1 p-1 bg-sonar-bg border border-sonar-border rounded-xl mb-8">
            <button 
              onClick={() => { setMode('signin'); setError(''); }}
              className={`flex-1 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${mode === 'signin' ? 'bg-sonar-green text-black' : 'text-slate-500'}`}
            >
              Sign In
            </button>
            <button 
              onClick={() => { setMode('signup'); setError(''); }}
              className={`flex-1 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${mode === 'signup' ? 'bg-sonar-green text-black' : 'text-slate-500'}`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <input 
                type="text" 
                placeholder="Full Name" 
                required 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                className="w-full px-4 py-3.5 bg-sonar-bg border border-sonar-border rounded-xl text-white text-sm font-mono outline-none focus:border-sonar-green" 
              />
            )}
            <input 
              type="email" 
              placeholder="Email Address" 
              required 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              className="w-full px-4 py-3.5 bg-sonar-bg border border-sonar-border rounded-xl text-white text-sm font-mono outline-none focus:border-sonar-green" 
            />
            <input 
              type="password" 
              placeholder="Password" 
              required 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              className="w-full px-4 py-3.5 bg-sonar-bg border border-sonar-border rounded-xl text-white text-sm font-mono outline-none focus:border-sonar-green" 
            />
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <SafeIcon icon={FiAlertCircle} className="text-red-400 shrink-0" />
                <p className="text-xs text-red-400 font-mono">{error}</p>
              </div>
            )}
            <button type="submit" disabled={loading} className="w-full py-4 bg-sonar-green text-black font-black uppercase tracking-widest text-sm rounded-xl flex items-center justify-center gap-3 hover:bg-sonar-green-glow disabled:opacity-60">
              {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : (
                <>
                  {mode === 'signin' ? 'Access Platform' : 'Create Account'}
                  <SafeIcon icon={FiArrowRight} />
                </>
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPage;